/**
 * Web sibling of OrnamentBackground.
 *
 * On the logged-out desktop frame the screen's teal hero merges into a
 * full-window ground (web/src/shell/WebShell.tsx). If the ornament stayed
 * inside the 640px column it would stop at the column edges and read as a
 * seam, so here the brand background (web/src/assets/brand-ground.png) is
 * pinned to the browser viewport and spans the whole window behind the
 * screen. In every other frame the mobile component renders unchanged.
 *
 * Place this component outside any ScrollView: react-native-web gives scroll
 * views a compositing transform, which would pin the image to the scroll view
 * instead of the window. Metro never picks `.web.*` files.
 */
import React from 'react';
import { Image, StyleSheet, View } from 'react-native';

import brandGround from '../../web/src/assets/brandGround';
import { useFrame } from '../../web/src/shell/frameContext';
import MobileOrnamentBackground from './OrnamentBackground.js';

interface OrnamentBackgroundProps {
  opacity?: number;
}

const OrnamentBackground: React.FC<OrnamentBackgroundProps> = ({ opacity = 0.9 }) => {
  const frame = useFrame();
  if (frame !== 'auth') return <MobileOrnamentBackground opacity={opacity} />;
  return (
    <View style={styles.viewport} pointerEvents="none">
      <Image
        source={{ uri: brandGround }}
        style={[styles.ground, { opacity }]}
        accessibilityElementsHidden
      />
    </View>
  );
};

const styles = StyleSheet.create({
  viewport: {
    // `fixed` is valid in react-native-web; the RN types only list the
    // native values, hence the cast.
    position: 'fixed' as unknown as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  ground: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
});

export default OrnamentBackground;
